import { getCSSVariable } from '../../stylesheets/cssVariablesUtils';
import { TagColor } from '../../muiComponents/Tag';
import {
    IContractAgeSegmentState,
    IContractDetailState,
    IContractMealPlanState,
    IContractRateDateState,
    IContractRoomState,
    IContractSeasonState,
    IContractTimelineSegment,
    IMainInfoContractDetailState,
} from '../../types/contracts/contractTypes';
import variables from '../../stylesheets/variables.module.scss';
import { getRandomNumber } from '../usefulFunctions';
import {
    IOccupancyRatePersonRate,
    IStaticRateDateOccupancyRateParsed,
    PERSON_TYPES,
} from '../../types/entities/IStaticRateDateOccupancyRateParsed';

export const RATE_SCOPE_MODEL_KEY_ICON_COLOR = {
    OCCUPANCY: getCSSVariable('accentColorOrange400'),
    ROOM: getCSSVariable('accentColorPurple400'),
    PERSON: getCSSVariable('accentColorCyan400'),
    ROOM_EXTRA: getCSSVariable('accentColorPink400'),
} as const;

export const RATE_SCOPE_MODEL_KEY_TAG_COLOR: Record<
    keyof typeof RATE_SCOPE_MODEL_KEY_ICON_COLOR,
    TagColor
> = {
    OCCUPANCY: 'orange',
    ROOM: 'purple',
    PERSON: 'cyan',
    ROOM_EXTRA: 'pink',
};

export const contractDetailViewModel_mainInfo = {
    Id: true,
    Enabled: true,
    IdStaticRateContractStatus: true,
    StaticRateContractStatus: {
        IdStaticRateContractStatusType: true,
        StaticRateContractStatusType: {
            Caption: true,
            LocalizedCaption: true,
            Name: true,
            Color: true,
        },
    },
    Name: true,
    Description: true,
    IdCurrency: true,
    Currency: {
        Name: true,
    },
    IdRateScopeModel: true,
    RateScopeModel: {
        LocalizedCaption: true,
        Caption: true,
        Name: true,
        Key: true,
    },
    IdSupplierHotel: true,
    SupplierHotel: {
        Name: true,
        AdultsOnly: true,
        ChildrenAgeSegments: {
            Age: true,
            Caption: true,
        },
    },
    IdAgency: true,
    Agency: {
        Name: true,
    },
    SupplierHotelRatePlan: { Name: true },
    IdSupplierHotelRatePlan: true,
    Key: true,
    ActiveFrom: true,
    ActiveTo: true,
};

export const contractDetailViewModel = {
    ...contractDetailViewModel_mainInfo,
    ActiveFrom: true,
    ActiveTo: true,
    Key: true,
    Seasons: {
        DateCreated: true,
        DateLastUpdated: true,
        Enabled: true,
        FromDate: true,
        Name: true,
        ToDate: true,
    },
    RoomTypes: {
        DateCreated: true,
        DateLastUpdated: true,
        Enabled: true,
        IdSupplierHotelRoomType: true,
        Name: true,
        SupplierHotelRoomType: {
            Name: true,
            StandardOccupancy: true,
            MaxOccupancy: true,
            MaxChild: true,
            MinChildren: true,
            Occupancy: true,
            IdRateScopeModel: true,
            RateScopeModel: {
                LocalizedCaption: true,
                Caption: true,
                Name: true,
                Key: true,
            },
            AdultOnly: true,
            MinAdults: true,
            MaxAdults: true,
        },
    },
    MealPlans: {
        DateCreated: true,
        DateLastUpdated: true,
        Enabled: true,
        IdSupplierHotelMealPlan: true,
        Name: true,
        SupplierHotelMealPlan: {
            Name: true,
            IsRoomOnly: true,
            IsBedAndBreakfast: true,
            MealPlanType: {
                Name: true,
            },
        },
    },
    RateDates: {
        BaseRate: true,
        CurrentInventory: true,
        DateLastUpdated: true,
        IdStaticRateRoomType: true,
        IdStaticRateSeason: true,
        IdStaticRateMealPlan: true,
        InitialInventory: true,
        MaxBookingAdvancedOffset: true,
        MaxStayAtArrival: true,
        MinBookingAdvancedOffset: true,
        MinStayAtArrival: true,
        OccupancyRates: true,
        StaticRateRoomType: {
            Name: true,
            SupplierHotelRoomType: {
                IdRateScopeModel: true,
                RateScopeModel: {
                    LocalizedCaption: true,
                    Caption: true,
                    Name: true,
                    Key: true,
                },
                StandardOccupancy: true,
                MaxOccupancy: true,
                MinAdults: true,
                MaxAdults: true,
                Occupancy: true,
                AdultOnly: true,
                MinChildren: true,
                MaxChild: true,
            },
        },
        StaticRateMealPlan: {
            Name: true,
        },
        StaticRateSeason: {
            Name: true,
        },
    },
    Policies: {
        Content: true,
        Id: true,
        Name: true,
    },
};

export const CONTRACT_DETAIL_INITIAL_STATE: IContractDetailState = {
    id: '',
    enabled: false,
    idStatus: 0,
    status: '',
    statusColor: '',
    name: '',
    description: '',
    idCurrency: 0,
    currency: '',
    idRateScopeModel: 0,
    rateScopeModel: '',
    rateScopeModelKey: '',
    idSupplierHotel: 0,
    supplierHotel: '',
    idAgency: 0,
    agency: '',
    idRatePlan: 0,
    ratePlan: '',
    key: '',
    activeFrom: null,
    activeTo: null,
    seasons: [],
    rooms: [],
    mealPlans: [],
    rateDates: [],
    policies: [],
    ageSegments: [],
};

export const orderSeasonsByStartDate = (seasons: IContractSeasonState[]) => {
    return seasons.sort((a, b) => {
        const aStart = a.start
            ? new Date(a.start).getTime()
            : Number.MAX_SAFE_INTEGER;
        const bStart = b.start
            ? new Date(b.start).getTime()
            : Number.MAX_SAFE_INTEGER;
        return aStart - bStart;
    });
};

export const ID_CONTRACT_STATUS_BLOCKED = 2;
export const EMPTY_SEASON_COLOR = '#DADADA';

export const seasonColors = [
    variables.accentColorCyan400,
    variables.accentColorYellow400,
    variables.accentColorOrange400,
    variables.accentColorPink400,
    variables.accentColorDarkBlue400,
    variables.accentColorPurple400,
];

export const getEmptyTimelineSegment = (
    start: Date,
    end: Date
): IContractTimelineSegment => ({
    id: null,
    name: '',
    start,
    end,
    color: EMPTY_SEASON_COLOR,
});

export const MAIN_INFO_INITIAL_STATE: IMainInfoContractDetailState = {
    id: '',
    enabled: false,
    key: '',
    idStatus: 0,
    status: '',
    statusColor: '',
    idAgency: 0,
    agency: '',
    idSupplierHotel: 0,
    supplierHotel: '',
    name: '',
    description: '',
    idRatePlan: 0,
    ratePlan: '',
    idRateScopeModel: 0,
    rateScopeModel: '',
    rateScopeModelKey: '',
    activeFrom: null,
    activeTo: null,
    idCurrency: 0,
    currency: '',
};

export const MAIN_INFO_VALIDATION_INITIAL_STATE = {
    idStatus: '',
    name: '',
    idSupplierHotel: '',
    idAgency: '',
    idRatePlan: '',
    idRateScopeModel: '',
    idCurrency: '',
    activeFrom: '',
    activeTo: '',
};

export const SEASON_INITIAL_STATE = {
    id: `-${getRandomNumber()}`,
    enabled: true,
    start: null,
    end: null,
    name: '',
};

export const SEASON_VALIDATION_INITIAL_STATE = {
    start: '',
    end: '',
    name: '',
};

export const ROOM_INITIAL_STATE = {
    id: `${getRandomNumber(-100, 0)}`,
    enabled: true,
    name: '',
    idSupplierHotelRoomType: 0,
    supplierHotelRoomType: '',
    maxOccupancy: 0,
    minAdults: 0,
    maxAdults: 0,
    adultsOnly: false,
    standardOccupancy: 0,
    occcupancy: '',
    idRateScopeModel: 0,
    rateScopeModel: '',
    minChildren: 0,
    maxChildren: 0,
    occupancy: '',
    rateScopeModelKey: '',
};

export const ROOM_VALIDATION_INITIAL_STATE = {
    name: '',
    idSupplierHotelRoomType: '',
};

export const MEAL_PLAN_INITIAL_STATE = {
    id: `${getRandomNumber(-100, 0)}`,
    enabled: true,
    name: '',
    idMealPlan: 0,
    mealPlan: '',
    isRoomOnly: false,
    isBedAndBreakfast: false,
    type: '',
};

export const MEAL_PLAN_VALIDATION_INITIAL_STATE = {
    name: '',
    idSupplierHotelMealPlan: '',
};

export const CANCELLATION_POLICY_INITIAL_STATE = {
    id: `${getRandomNumber(-100, 0)}`,
    content: '',
    name: '',
};

export const RATE_DATE_INITIAL_STATE: IContractRateDateState = {
    id: `${getRandomNumber(-100, 0)}`,
    idRoom: 0,
    room: '',
    occupancy: '',
    maxOccupancy: 0,
    adultsOnly: false,
    standardOccupancy: 0,
    minAdults: 0,
    maxAdults: 0,
    minChildren: 0,
    maxChildren: 0,
    idRoomScopeModel: 0,
    roomScopeModel: '',
    roomScopeModelKey: '',
    idRateScopeModel: 0,
    rateScopeModel: '',
    rateScopeModelKey: '',
    idSeason: 0,
    season: '',
    idMealPlan: 0,
    mealPlan: '',
    baseRate: 0,
    currentInventory: 0,
    initialInventory: 0,
    maxBookingAdvancedOffset: 0,
    maxStayAtArrival: 0,
    minBookingAdvancedOffset: 0,
    minStayAtArrival: 0,
    occupancyRates: {} as IStaticRateDateOccupancyRateParsed,
};

export const CANCELLATION_POLICY_VALIDATION_INITIAL_STATE = {
    content: '',
    name: '',
};

export const RATE_BY_ROOM_INITIAL_STATE = {
    abt: 0,
    aat: 0,
};

export const RATE_OCCUPANCY_INITIAL_STATE = {
    model: 0,
    room: {},
    persons: {
        singleUseAllowed: false,
        rates: [],
    },
    occupancies: [],
};

export const getInitialStateOccupancyRate = (
    idRateScopeModel: number,
    rateScopeModel: string,
    room: IContractRoomState,
    mealPlan: IContractMealPlanState,
    season: IContractSeasonState,
    ageSegments: IContractAgeSegmentState[]
): IContractRateDateState => {
    const scopeModel = room.idRateScopeModel || idRateScopeModel;
    const occupancy = room.occupancy;
    const occupancyArray = occupancy
        .split('|')
        .map(occ => occ.split('.').map(per => parseInt(per)));

    const { maxAdults, maxChildren } = occupancyArray.reduce(
        (acc, [adults, children]) => {
            return {
                maxAdults: Math.max(acc.maxAdults, adults),
                maxChildren: Math.max(acc.maxChildren, children),
            };
        },
        { maxAdults: 0, maxChildren: 0 }
    );
    const orderedAgeSegments = ageSegments.sort((a, b) => {
        return a.age - b.age;
    });

    const {
        minAdults,
        standardOccupancy,
        maxAdults: maxAdultsRoom,
        minChildren: minChildrenRoom,
        maxChildren: maxChildrenRoom,
    } = room;
    const singleUseAllowed = minAdults === 1 && standardOccupancy > 1;
    ///si el min adults es 1 => se permite single use => personIndex = 0
    // sino desde standard occupancy personIndex = 1 hasta max adults => personIndex = adults

    const ratesPerPersonAdults = [];
    if (singleUseAllowed) {
        // Single use → personIndex = 0
        ratesPerPersonAdults.push({
            personType: PERSON_TYPES.ADULT,
            personIndex: 0,
            ageSegment: 0,
            abt: 0,
            aat: 0,
        });
    }

    ratesPerPersonAdults.push({
        personType: PERSON_TYPES.ADULT,
        personIndex: 1,
        ageSegment: 0,
        abt: 0,
        aat: 0,
    });

    //  Resto de adultos → personIndex = nº real
    for (
        let adults = standardOccupancy + 1;
        adults <= maxAdultsRoom;
        adults++
    ) {
        ratesPerPersonAdults.push({
            personType: PERSON_TYPES.ADULT,
            personIndex: adults,
            ageSegment: 0,
            abt: 0,
            aat: 0,
        });
    }

    let ratesPerPersonChildren: IOccupancyRatePersonRate[] = [];
    if (!room.adultsOnly && orderedAgeSegments.length) {
        orderedAgeSegments.forEach(seg => {
            const array = Array.from({
                length: maxChildrenRoom - minChildrenRoom,
            }).map((_, indexChi) => {
                return {
                    personType: PERSON_TYPES.CHILD,
                    personIndex: minChildrenRoom + indexChi + 1,
                    ageSegment: seg.id,
                    abt: 0,
                    aat: 0,
                };
            });
            ratesPerPersonChildren = [...ratesPerPersonChildren, ...array];
        });
    }

    return {
        id: `${getRandomNumber(-100, 0)}`,
        idRoom: parseInt(room.id),
        room: room.name,
        idSeason: parseInt(season.id),
        season: season.name,
        idMealPlan: parseInt(mealPlan.id),
        mealPlan: mealPlan.name,
        occupancy: room.occupancy,
        maxOccupancy: room.maxOccupancy,
        standardOccupancy: room.standardOccupancy,
        minAdults: room.minAdults,
        maxAdults: room.maxAdults,
        minChildren: room.minChildren,
        maxChildren: room.maxChildren,
        adultsOnly: room.adultsOnly,
        idRoomScopeModel: room.idRateScopeModel,
        roomScopeModel: room.rateScopeModel,
        roomScopeModelKey: room.rateScopeModelKey,
        idRateScopeModel,
        rateScopeModel,
        rateScopeModelKey: room.rateScopeModelKey,
        baseRate: 0,
        currentInventory: 0,
        initialInventory: 0,
        maxBookingAdvancedOffset: 0,
        maxStayAtArrival: 0,
        minBookingAdvancedOffset: 0,
        minStayAtArrival: 0,
        occupancyRates: {
            model: scopeModel,
            room: { ...RATE_BY_ROOM_INITIAL_STATE },
            persons: {
                singleUseAllowed: singleUseAllowed,
                rates:
                    scopeModel === RATE_SCOPE_MODELS.PERSON
                        ? [...ratesPerPersonAdults, ...ratesPerPersonChildren]
                        : [],
            },
            occupancies:
                scopeModel === RATE_SCOPE_MODELS.OCCUPANCY
                    ? room.occupancy.split('|').map(occ => {
                          const [adults, children] = occ.split('.');
                          return {
                              adults: parseInt(adults),
                              children: parseInt(children),
                              abt: 0,
                              aat: 0,
                          };
                      })
                    : [],
        },
    };
};

export const convertOccupancyRateStringToJSON = (occRate: string | null) => {
    return occRate ? JSON.parse(occRate) : RATE_OCCUPANCY_INITIAL_STATE;
};

export const RATE_SCOPE_MODELS = {
    ROOM: 2,
    OCCUPANCY: 1,
    PERSON: 3,
};

export const AGE_SEGMENTS = {
    BABY: 1,
    FIRST: 2,
    SECOND: 3,
    THIRD: 4,
};

export const AGE_SEGMENTS_ICONS = {
    1: 'ChildFriendly',
    2: 'ChildCare',
    3: 'Face',
    4: 'Face3',
};
